import {Backdrop, Box, CircularProgress, Typography, IconButton} from '@mui/material';
import {useEffect, useState} from 'react';
import {CustomLinearProgress} from './customLoaders';
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faVolume, faVolumeSlash} from '@fortawesome/pro-light-svg-icons';
import {useMediaQuery} from '@mui/material';

/**
 * Renders a loading screen with a progress bar.
 *
 * @param {Object} props
 * @param {string} props.title - The title of the loading screen. Default is "Loading".
 * @param {number} props.secondsToLoad - The number of seconds it takes to complete the loading process.
 * @param {boolean} props.semiTransparent - Determines if the loading screen should be semi-transparent. Default is false.
 * @param {boolean} props.isAlmostComplete - Should be a useState boolean value that tells if the API call (assuming that's the reason you are showing a loading screen) has finished, in that case then the progress will go faster to a 100% This also assumes that you don't redirect the user immediately after the API call finishes but you delay that for a couple of seconds so that this loading screen arrives at 100% and then you show whatever you want to show after this loading screen. For this to work as mentioned, you should delay your redirection for 2 seconds approx and before those 2 seconds you should set the isAlmostComplete variable to true.
 *
 * @returns {JSX.Element} The loading screen component.
 *
 * @throws {Error} If the secondsToLoad parameter is not a number.
 *
 */
export const LoadingScreenWithProgressBar = ({title, secondsToLoad, semiTransparent, isAlmostComplete}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    //whenever this component renders for the first time it should always be empty in terms of progress
    setProgress(0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress === 100) return oldProgress;

        let diff = (Math.random() * 100) / (secondsToLoad * 1.03);
        if (isAlmostComplete) {
          if (oldProgress < 95) return 95;
          return Math.min(oldProgress + 3, 100);
        }

        const output = Math.min(oldProgress + diff, 100);
        return output;
      });
    }, 500);
    return () => {
      if (timer !== undefined) clearInterval(timer);
    };
  }, [isAlmostComplete]);

  return (
    <Backdrop
      open={true}
      sx={{
        zIndex: 9999,
        color: '#fff',
        backgroundColor: semiTransparent ?? '#7f7f7f',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant={'h5'} sx={{userSelect: 'none', fontSize: {xs: 20, sm: 24}, pb: 1}}>
        {title ?? 'Loading'}
      </Typography>
      <Box sx={{width: {xs: '60%', md: '40%', lg: '25%'}, py: 3}}>
        <CustomLinearProgress variant="determinate" value={progress} />
      </Box>
    </Backdrop>
  );
};

/**
 * Renders a loading screen component.
 *
 * @param {Object} props - The title to display on the loading screen. Defaults to "Loading..." if not provided.
 * @param {string} props.title - The title to display on the loading screen. Defaults to "Loading..." if not provided.
 * @param {boolean} props.semiTransparent - Determines whether the loading screen should have a semi-transparent background. Defaults to false if not provided.
 *
 * @returns {ReactElement} The loading screen component.
 *
 */
export const LoadingScreen = ({title, semiTransparent}) => {
  return (
    <Backdrop
      open={true}
      sx={{
        zIndex: 9999,
        color: '#fff',
        backgroundColor: semiTransparent ?? '#7f7f7f',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant={'h5'} sx={{userSelect: 'none', fontSize: {xs: 20, sm: 24}, pb: 1}}>
        {title ?? 'Loading...'}
      </Typography>
      <CircularProgress color="inherit" size={80} thickness={4} />
    </Backdrop>
  );
};

/**
 * AnimationLoadingScreen component for displaying a loading screen with animation/video.
 *
 * @component
 * @returns {JSX.Element} The AnimationLoadingScreen component.
 */
export const AnimationLoadingScreen = () => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Backdrop
      open={true}
      sx={{
        zIndex: 9999,
        color: '#fff',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <img
        src="/images/floss-animation.gif"
        alt="Loading Animation"
        width={isSmallScreen ? '300' : '400'}
        height={isSmallScreen ? '200' : '224'}
      />
    </Backdrop>
  );
};

/**
 * AnimationLoadingScreenWithProgressBar component for displaying a loading screen with animation/video and progress bar.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {string} [props.title] - The title displayed during loading.
 * @param {number} [props.secondsToLoad] - The time in seconds for the loading to complete.
 * @param {string} [props.semiTransparent] - The background color of the loading screen.
 * @param {boolean} [props.isAlmostComplete] - Indicates whether the loading is almost complete.
 * @returns {JSX.Element} The AnimationLoadingScreenWithProgressBar component.
 */
export const AnimationLoadingScreenWithProgressBar = ({title, secondsToLoad, semiTransparent, isAlmostComplete}) => {
  const [progress, setProgress] = useState(0);
  const [isAudioMuted, setIsAudioMuted] = useState(true);

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const toggleAudio = () => {
    setIsAudioMuted(prev => !prev);
  };

  useEffect(() => {
    //whenever this component renders for the first time it should always be empty in terms of progress
    setProgress(0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress === 100) return oldProgress;

        let diff = (Math.random() * 100) / (secondsToLoad * 1.03);
        if (isAlmostComplete) {
          if (oldProgress < 95) return 95;
          return Math.min(oldProgress + 3, 100);
        }

        const output = Math.min(oldProgress + diff, 100);
        return output;
      });
    }, 500);
    return () => {
      if (timer !== undefined) clearInterval(timer);
    };
  }, [isAlmostComplete]);

  return (
    <Backdrop
      open={true}
      sx={{
        zIndex: 9999,
        color: '#fff',
        backgroundColor: semiTransparent ?? 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <IconButton style={{position: 'absolute', top: '10px', right: '10px', cursor: 'pointer'}} onClick={toggleAudio}>
        <FontAwesomeIcon icon={isAudioMuted ? faVolumeSlash : faVolume} style={{color: '#3DA9F8'}} />
      </IconButton>
      <video
        autoPlay
        loop
        muted={isAudioMuted}
        width={isSmallScreen ? '300' : '400'}
        height={isSmallScreen ? '200' : '300'}
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        controlsList="nodownload"
        style={{
          outline: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <source src="/videos/floss-animation.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <Box sx={{width: {xs: '60%', md: '40%', lg: '20%'}, py: 3}}>
        <CustomLinearProgress variant="determinate" value={progress} />
      </Box>
      <Typography
        variant={'h5'}
        color={'primary'}
        sx={{userSelect: 'none', fontSize: {xs: 18, sm: 20}, pb: 1, fontWeight: 500}}
      >
        {title ?? 'Loading...'}
      </Typography>
    </Backdrop>
  );
};
